import { format } from 'date-fns';
import { transformDocumentNumber } from '@/components/custom/erp';
import { isValidUUID } from '@/lib/uuid-validator';
import { FormFieldState } from '../hooks';

/**
 * Transform detail rows for API submission
 * @param detailRows - Array of detail row data from the form
 * @returns Transformed detail rows ready for API submission
 */
export const transformDetailRows = (detailRows: MuaHangRecord[]) => {
  return detailRows.map((row: MuaHangRecord, index: number) => ({
    line: index + 1,
    ...(isValidUUID(row.uuid) ? { uuid: row.uuid } : {}),
    ma_vt: row.ma_vt_data?.uuid || row.ma_vt || '',
    dvt: row.dvt || row.ma_vt_data?.dvt || '',
    ma_kho: row.ma_kho_data?.uuid || row.ma_kho || '',
    ma_lo: row.ma_lo_data?.uuid || row.ma_lo || '',
    lo_yn: row.lo_yn || false,
    ma_vi_tri: row.ma_vi_tri_data?.uuid || row.ma_vi_tri || '',
    vi_tri_yn: row.vi_tri_yn || false,
    he_so: row.he_so || 1,
    qc_yn: row.qc_yn || false,
    so_luong: row.so_luong || 0,
    gia_nt: row.gia_nt || 0,
    tien_nt: row.tien_nt || 0,
    thue_nt: row.thue_nt || 0,
    gia: row.gia || 0,
    tien: row.tien || 0,
    thue: row.thue || 0,
    tk_vt: row.tk_vt_data?.uuid || '',
    ma_bp: row.ma_bp_data?.uuid || row.ma_bp || '',
    ma_vv: row.ma_vv_data?.uuid || row.ma_vv || '',
    ma_hd: row.ma_hd_data?.uuid || row.ma_hd || '',
    ma_dtt: row.ma_dtt_data?.uuid || row.ma_dtt || '',
    ma_ku: row.ma_ku_data?.uuid || row.ma_ku || '',
    ma_phi: row.ma_phi_data?.uuid || row.ma_phi || '',
    ma_sp: row.ma_sp_data?.uuid || row.ma_sp || '',
    ma_lsx: row.ma_lsx_data?.uuid || row.ma_lsx || '',
    ma_cp0: row.ma_cp0_data?.uuid || row.ma_cp0 || '',

    id_hd: row.id_hd?.uuid,
    line_hd: row.line_hd?.uuid
  }));
};

/**
 * Transform all form data for submission
 * @param data - Form data from the form submission
 * @param state - Form field state containing references to selected entities
 * @param detailRows - Array of detail row data
 * @returns Transformed data ready for API submission
 */
export const transformFormData = (
  data: MuaHangRecord,
  state: FormFieldState,
  detailRows: MuaHangRecord[],
  entityUnit: MuaHangRecord,
  totalResults: MuaHangRecord
) => {
  const detail = transformDetailRows(detailRows);

  return {
    ...data,
    unit_id: entityUnit?.uuid || '',
    transfer_yn: state.transfer_yn,
    outvat_yn: state.outvat_yn,
    lo_yn: state.lo_yn,
    vi_tri_yn: state.vi_tri_yn,
    qc_yn: state.qc_yn,
    ma_ngv: '3',
    ma_kh: state.nhaCungCap?.uuid || '',
    ma_so_thue: data.ma_so_thue || state.nhaCungCap?.tax_code || null,
    ten_kh_thue: data.ten_kh_thue || state.nhaCungCap?.customer_name || '',
    dia_chi: data.dia_chi || state.nhaCungCap?.address || '',
    ong_ba: data.ong_ba || state.nhaCungCap?.contact_person || '',
    e_mail: data.e_mail || state.nhaCungCap?.email || '',
    dien_giai: data.dien_giai || '',
    ma_nx: state.nhapXuat?.uuid || '',
    ma_nvbh: state.nhapXuat?.uuid || '',
    tk: state.taiKhoan?.uuid || state.nhaCungCap?.account_data?.uuid || '',
    ma_tt: state.hanThanhToan?.uuid || state.nhaCungCap?.payment_term_data?.uuid || '',
    ma_nt: data.ma_nt || '',
    ...transformDocumentNumber(state.quyenChungTu, state.soChungTu),
    ma_thue: state.taxModel?.uuid || '',
    tk_thue: state.taiKhoanThue?.uuid || '',
    thue_suat: state.taxModel?.thue_suat || 0,
    ma_kh9: state.cucThue?.uuid || '',
    ma_tc_thue: state.tinhChatThue?.uuid || '',
    ngay_ct: format(data.ngay_ct, 'yyyy-MM-dd') || '',
    ngay_ct0: format(data.ngay_ct0, 'yyyy-MM-dd') || '',
    so_ct_hddt: data.so_ct_hddt || '',
    ngay_ct_hddt: data.ngay_ct_hddt || '',
    so_ct2_hddt: data.so_ct2_hddt || '',
    ma_mau_ct_hddt: data.ma_mau_ct_hddt || '',
    ma_tthddt: data.ma_tthddt || '0',
    ma_pttt: data.ma_pttt || '',
    ma_gd: data.ma_gd || 'TL',
    ma_mau_bc: data.ma_mau_bc || '',
    ten_vt_thue: data.ten_vt_thue || '',
    ghi_chu: data.ghi_chu || '',
    t_so_luong: totalResults.so_luong,
    t_tien_nt: totalResults.thanh_tien,
    t_tien: totalResults.thanh_tien,
    t_thue_nt: totalResults.thue,
    t_thue: totalResults.thue,
    t_tt_nt: totalResults.tong_tien,
    t_tt: totalResults.tong_tien,
    ty_gia: data.ty_gia || 1,
    status: data.status || '5',
    chi_tiet: detail
  };
};
